import React from 'react'
import { Link } from 'react-router-dom'

import { ArrowRight, Clock } from 'lucide-react'

import {
  type ExplorerTxRecord,
  TX_LIST_DISPLAY_ASSET,
  getTxDisplayAction,
  getTxDisplayAmount,
} from '@/shared/api/client'
import { useIsMobile } from '@/shared/hooks/useIsMobile'
import { formatUTC, truncateMiddle } from '@/shared/lib/format'
import ActionBadge from '@/shared/ui/ActionBadge'
import CopyButton from '@/shared/ui/CopyButton'
import HashLink from '@/shared/ui/HashLink'

export type TxTableColumn =
  | 'hash'
  | 'action'
  | 'method'
  | 'block'
  | 'time'
  | 'age'
  | 'address'
  | 'from'
  | 'to'
  | 'direction'
  | 'asset'
  | 'amount'

interface TxTableProps {
  rows: ExplorerTxRecord[]
  columns?: TxTableColumn[]
  contextAddress?: string
  emptyText?: string
  /** 自定义列宽百分比，不传则均分。如 `{ hash: '28%', amount: '25%' }` */
  columnWidths?: Partial<Record<TxTableColumn, string>>
}

const headerCellStyle: React.CSSProperties = {
  padding: 'var(--space-14) var(--space-2xl)',
  color: 'var(--app-text-tertiary)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const bodyCellStyle: React.CSSProperties = {
  padding: 'var(--space-14) var(--space-2xl)',
  color: 'var(--app-text-primary)',
  fontSize: 'var(--font-size-md)',
  whiteSpace: 'nowrap',
}

const COLUMN_LABELS: Record<TxTableColumn, string> = {
  hash: 'Transaction Hash',
  action: 'Action',
  method: 'Action',
  block: 'Block',
  time: 'DateTime (UTC)',
  age: 'Age',
  address: 'Address',
  from: 'Address',
  to: 'To',
  direction: '',
  asset: 'Asset',
  amount: 'Amount',
}

type TxDirection = 'IN' | 'OUT'

const getTxDirection = (tx: ExplorerTxRecord, contextAddress?: string): TxDirection | null => {
  if (!contextAddress) return null
  const ctx = contextAddress.toLowerCase()
  const from = tx.wallet_address?.toLowerCase()
  const to = tx.to_address?.toLowerCase()
  if (to === ctx && from !== ctx) return 'IN'
  if (from === ctx) return 'OUT'
  return null
}

const DirectionBadge: React.FC<{ direction: TxDirection }> = ({ direction }) => {
  const isOut = direction === 'OUT'
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-xxs)',
        padding: 'var(--space-xxs) var(--space-sm)',
        borderRadius: 'var(--radius-xs)',
        fontSize: 'var(--font-size-2xs)',
        fontWeight: 700,
        letterSpacing: 'var(--tracking-badge)',
        backgroundColor: isOut ? 'var(--app-down-soft)' : 'var(--app-up-soft)',
        color: isOut ? 'var(--app-down)' : 'var(--app-up)',
        border: `1px solid ${isOut ? 'var(--app-down-soft)' : 'var(--app-up-soft)'}`,
        whiteSpace: 'nowrap',
      }}
    >
      {direction}
      <ArrowRight size={10} />
    </span>
  )
}

const TxCard: React.FC<{ tx: ExplorerTxRecord; columns: TxTableColumn[]; contextAddress?: string }> = ({
  tx,
  columns,
  contextAddress,
}) => {
  const rows: { label: string; content: React.ReactNode }[] = []

  for (const col of columns) {
    switch (col) {
      case 'hash':
        rows.push({
          label: 'HASH',
          content: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', minWidth: 0 }}>
              <Link
                to={`/tx/${tx.tx_hash}`}
                style={{
                  color: 'var(--accent-cyan)',
                  fontFamily: 'monospace',
                  fontSize: 'var(--font-size-md)',
                  textDecoration: 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {truncateMiddle(tx.tx_hash)}
              </Link>
              <CopyButton text={tx.tx_hash} size={12} label="Copy hash" />
            </div>
          ),
        })
        break
      case 'action':
        rows.push({
          label: 'Action',
          content: <ActionBadge label={getTxDisplayAction(tx)} fontSize="12px" padding="3px 8px" />,
        })
        break
      case 'block':
        rows.push({
          label: 'Block',
          content: <HashLink kind="block" value={tx.block_height} copyable={false} fontSize="var(--font-size-md)" />,
        })
        break
      case 'time':
        rows.push({
          label: 'Time (UTC)',
          content: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', minWidth: 0 }}>
              <Clock size={12} style={{ color: 'var(--app-text-secondary)', flexShrink: 0 }} />
              <span
                style={{
                  color: 'var(--app-text-primary)',
                  fontSize: 'var(--font-size-md)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatUTC(tx.block_time)}
              </span>
            </div>
          ),
        })
        break
      case 'address':
      case 'from':
        rows.push({
          label: 'Address',
          content: <HashLink kind="address" value={tx.wallet_address} fontSize="var(--font-size-md)" />,
        })
        break
      case 'to':
        rows.push({
          label: 'To',
          content: tx.to_address ? (
            <HashLink kind="address" value={tx.to_address} fontSize="var(--font-size-md)" />
          ) : (
            <span style={{ color: 'var(--app-text-secondary)' }}>—</span>
          ),
        })
        break
      case 'direction': {
        const dir = getTxDirection(tx, contextAddress)
        rows.push({
          label: '',
          content: dir ? (
            <DirectionBadge direction={dir} />
          ) : (
            <span style={{ color: 'var(--app-text-secondary)' }}>—</span>
          ),
        })
        break
      }
      case 'asset':
        rows.push({
          label: 'Asset',
          content: (
            <span style={{ color: 'var(--app-text-primary)', fontSize: 'var(--font-size-md)', fontWeight: 500 }}>
              {TX_LIST_DISPLAY_ASSET}
            </span>
          ),
        })
        break
      case 'amount': {
        const raw = getTxDisplayAmount(tx, 999)
        const absAmount = raw.replace(/^[+-]/, '')
        const dir = getTxDirection(tx, contextAddress)
        const display = tx.biz_type === 'TRANSFER'
  ? (dir === 'OUT' ? `-${absAmount}` : absAmount)
  : raw
        rows.push({
          label: 'Amount',
          content: (
            <span
              style={{
                fontSize: 'var(--font-size-md)',
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={display}
            >
              {display}
            </span>
          ),
        })
        break
      }
      default:
        break
    }
  }

  return (
    <div
      style={{
        backgroundColor: 'var(--app-card-bg)',
        borderRadius: 'var(--radius-control)',
        border: '0.5px solid var(--app-card-border)',
        padding: '0',
        overflow: 'hidden',
      }}
    >
      {rows.map(({ label, content }, i) => (
        <div
          key={label}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: 'var(--space-10) var(--space-lg)',
            borderBottom: i < rows.length - 1 ? '0.5px solid var(--app-hairline-subtle)' : 'none',
          }}
        >
          <span
            style={{
              color: 'var(--app-text-secondary)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              minWidth: 'var(--tx-table-direction-width)',
              flexShrink: 0,
              lineHeight: 'var(--line-height-sm)',
            }}
          >
            {label}
          </span>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              overflow: 'hidden',
              lineHeight: 'var(--line-height-sm)',
            }}
          >
            {content}
          </div>
        </div>
      ))}
    </div>
  )
}

export { TxCard }

const TxTable: React.FC<TxTableProps> = ({
  rows,
  columns = ['hash', 'action', 'block', 'time', 'address', 'amount'],
  contextAddress,
  emptyText = 'No records.',
  columnWidths,
}) => {
  const isMobile = useIsMobile()

  if (isMobile) {
    if (rows.length === 0) {
      return (
        <div className="empty-state" style={{ padding: 'var(--space-3xl)' }}>
          {emptyText}
        </div>
      )
    }
    return (
      <div style={{ padding: 'var(--space-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--space-10)' }}>
        {rows.map((tx) => (
          <TxCard key={tx.id} tx={tx} columns={columns} contextAddress={contextAddress} />
        ))}
      </div>
    )
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', tableLayout: 'fixed' }}>
        <thead>
          <tr
            style={{ backgroundColor: 'var(--fill-secondary)', borderBottom: '0.5px solid var(--app-hairline-subtle)' }}
          >
            {columns.map((c) => {
              const w = columnWidths?.[c] || `${100 / columns.length}%`
              const align = c === 'amount' ? { textAlign: 'right' as const } : {}
              return (
                <th key={c} style={{ ...headerCellStyle, width: w, ...align }}>
                  {COLUMN_LABELS[c]}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{
                  ...bodyCellStyle,
                  color: 'var(--app-text-secondary)',
                  textAlign: 'center',
                }}
              >
                {emptyText}
              </td>
            </tr>
          ) : (
            rows.map((tx, index) => {
              const isLast = index === rows.length - 1
              const rowStyle: React.CSSProperties = {
                borderBottom: isLast ? 'none' : '0.5px solid var(--app-hairline-subtle)',
              }
              return (
                <tr key={tx.id} style={rowStyle} className="hover-row">
                  {columns.map((c) => {
                    switch (c) {
                      case 'hash':
                        return (
                          <td key={c} style={bodyCellStyle}>
                            <HashLink kind="tx" value={tx.tx_hash} />
                          </td>
                        )
                      case 'action':
                        return (
                          <td key={c} style={bodyCellStyle}>
                            <ActionBadge label={getTxDisplayAction(tx)} />
                          </td>
                        )
                      case 'block':
                        return (
                          <td key={c} style={bodyCellStyle}>
                            <HashLink kind="block" value={tx.block_height} copyable={false} />
                          </td>
                        )
                      case 'time':
                        return (
                          <td key={c} style={bodyCellStyle}>
                            {formatUTC(tx.block_time)}
                          </td>
                        )
                      case 'address':
                      case 'from':
                        return (
                          <td key={c} style={bodyCellStyle}>
                            <HashLink kind="address" value={tx.wallet_address} />
                          </td>
                        )
                      case 'to':
                        return (
                          <td key={c} style={bodyCellStyle}>
                            {tx.to_address ? (
                              <HashLink kind="address" value={tx.to_address} />
                            ) : (
                              <span style={{ color: 'var(--app-text-secondary)' }}>—</span>
                            )}
                          </td>
                        )
                      case 'direction': {
                        const dir = getTxDirection(tx, contextAddress)
                        return (
                          <td key={c} style={{ ...bodyCellStyle, textAlign: 'center' }}>
                            {dir ? <DirectionBadge direction={dir} /> : null}
                          </td>
                        )
                      }
                      case 'asset':
                        return (
                          <td key={c} style={bodyCellStyle}>
                            {TX_LIST_DISPLAY_ASSET}
                          </td>
                        )
                      case 'amount': {
                        const raw = getTxDisplayAmount(tx, 8)
                        const absAmount = raw.replace(/^[+-]/, '')
                        const dir = getTxDirection(tx, contextAddress)
                        const display = tx.biz_type === 'TRANSFER'
  ? (dir === 'OUT' ? `-${absAmount}` : absAmount)
  : raw
                        return (
                          <td
                            key={c}
                            style={{
                              ...bodyCellStyle,
                              fontSize: 'var(--font-size-md)',
                              fontVariantNumeric: 'tabular-nums',
                              textAlign: 'right',
                              whiteSpace: 'nowrap',
                            }}
                            title={display}
                          >
                            {display}
                          </td>
                        )
                      }
                    }
                  })}
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}

export default TxTable
